import {
  Box,
  Center,
  HStack,
  NumberInput,
  NumberInputField,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import AppRoutes from 'config/AppRoutes';
import PageLayout from 'ui/layout/PageLayout';
import { useTranslation } from 'react-i18next';
import { useRef, useState, useEffect } from 'react';
import Button from 'ui/molecules/Button';
import { useDispatch, useSelector } from 'react-redux';
import { isB2BSelector, RoofAngleSelector } from 'store/selectors';
import { updateRoofAngle } from 'store/answers/answersSlice';
import VideoBlock from 'components/VideoBlock';
import config from 'config/config';
import getValidVideoAngle from 'features/utils/getValidVideoAngle';
import useStepNavigation from 'utils/hooks/useStepNavigation';
import useAsset, { AssetType } from 'utils/hooks/useAsset';

const PageRoofAngle = () => {
  const { t } = useTranslation();
  const currentRoute = AppRoutes.ROOF_ANGLE;
  const isB2B = useSelector(isB2BSelector);
  const { navigateToNextStep, navigateToPreviousStep } =
    useStepNavigation(currentRoute);

  const numberInputRef = useRef<HTMLInputElement>(null);
  const userAngle = useSelector(RoofAngleSelector);
  const userAngleGiven: boolean = userAngle !== undefined;

  const dispatch = useDispatch();
  const [currentAngle, setAngle] = useState<number>(
    userAngleGiven ? userAngle : config.options.roofAngleDefault
  );
  const { isOpen: isCustomAngle, onOpen, onClose } = useDisclosure();

  const validAngle = getValidVideoAngle(currentAngle);
  const videoUrl = useAsset(
    AssetType.VIDEO,
    `/02-inclinaison/INCLINAISON ${
      validAngle === 0 ? 'PLAT' : validAngle
    } 1000.mp4`
  );

  // on component load, check if we have a custom user angle, then open the custom angle popup
  useEffect(() => {
    if (
      userAngleGiven &&
      !config.options.roofAngles.some(({ value }) => value === userAngle)
    ) {
      onOpen();
    }
  }, [userAngle, onOpen, userAngleGiven]);

  // --------------------- methods ------------------------

  const changeAngle = (angle: number) => {
    setAngle(angle);
    dispatch(updateRoofAngle(angle));
  };

  const handleSelectAll = () => {
    if (numberInputRef.current) {
      numberInputRef.current.value = '';
      numberInputRef.current.selectionStart = 0;
      numberInputRef.current.selectionEnd =
        numberInputRef.current.value.length + 1;
      numberInputRef.current.select();
    }
  };

  const handleContinue = () => {
    if (!userAngleGiven) {
      // be sure to continue with current selection if user did not change it
      changeAngle(config.options.roofAngleDefault);
    }
    navigateToNextStep();
  };

  const showPreciseAngleModal = () => {
    onOpen();
  };

  // --------------------- effects ------------------------

  // --------------------- render ------------------------

  return (
    <PageLayout
      title={t('ROOF_ANGLE_title')}
      buttonLabel={t('common.continue')}
      buttonCallback={handleContinue}
      backButton
      backCallback={isCustomAngle ? onClose : navigateToPreviousStep}
    >
      {!isCustomAngle && (
        <>
          <Box height={{ base: '200px', md: '300px' }} width="full">
            <VideoBlock
              loop={false}
              mt={{ base: '-100px', md: '-100px' }}
              position="relative"
              videoUrl={videoUrl}
              height={{ base: '400px', md: '500px' }}
              width={{ base: '400px', md: '500px' }}
              transform={
                isB2B
                  ? {
                      base: 'scale(1.1) translateY(10px)',
                      md: 'scale(1.5) translateY(10px)',
                    }
                  : ''
              }
            />
          </Box>

          <HStack
            boxShadow="blur"
            display="flex"
            p="5px"
            width="auto"
            borderRadius="100px"
            overflow="hidden"
            justifyContent="space-between"
          >
            {config.options.roofAngles.map((item) => {
              const itemLabel = t(`ROOF_ANGLE_OPTION_${item.key}`);
              const selectedOption = config.options.roofAngles.find(
                (op) => op.value === currentAngle
              );
              const isSelected = item.key === selectedOption?.key;
              return (
                <Box key={itemLabel}>
                  <Button
                    variant={isSelected ? 'radio-selected' : 'radio'}
                    onClick={() => changeAngle(item.value)}
                  >
                    <Text padding={{ base: 2, md: 7 }}>{itemLabel}</Text>
                  </Button>
                </Box>
              );
            })}
          </HStack>
          <Button variant="skip" onClick={showPreciseAngleModal}>
            {t('ROOF_ANGLE_OPTION_precise_button')}
          </Button>
        </>
      )}

      {isCustomAngle && (
        <Center display="flex" flexDirection="column">
          <Text pb="10px" textAlign="center">
            {t('ROOF_ANGLE_OPTION_other_label')}
          </Text>

          <NumberInput
            max={180}
            step={0}
            value={` ${currentAngle || 0}°`}
            min={0}
            focusInputOnChange
            onChange={(_val, num) => changeAngle(num)}
            focusBorderColor="primaryColor"
            borderColor="primaryColor"
          >
            <NumberInputField
              pt="10px"
              mt="0px"
              ref={numberInputRef}
              lineHeight="80px"
              height="80px"
              userSelect="all"
              fontSize={60}
              textAlign="center"
              maxW="250px"
              onFocus={handleSelectAll}
              onClick={handleSelectAll}
            />
          </NumberInput>
        </Center>
      )}
    </PageLayout>
  );
};

export default PageRoofAngle;
